"use client"

import { useState, useRef, useCallback, useEffect, useSyncExternalStore } from "react"
import { flushSync } from "react-dom"
import { toast } from "react-hot-toast"
import { ensureWebSocketReady, getSocket } from "../services/websocketService"
import MicPermissionModal from "../components/ui/mic-permission-modal"
import DisplayAudioModal from "../components/ui/display-audio-modal"
import {
  acquireMicrophoneStream,
  buildDisplayMediaOptions,
  detectCaptureEnvironment,
  DISPLAY_MEDIA_OPTIONS_FALLBACK,
} from "../utils/captureEnvironment"

/** @typedef {'input' | 'output' | 'both'} AudioSourceMode */

/**
 * @typedef {Object} StartRecordingOptions
 * @property {AudioSourceMode} [audioSourceMode='input'] - 'input' = mic only, 'output' = computer audio only, 'both' = mic + computer
 * @property {string} [audioInputDeviceId] - Optional deviceId from enumerateDevices (audioinput)
 * @property {MediaStream} [preacquiredDisplayStream] - Display stream from getDisplayMedia; must be acquired synchronously from the user click
 * @property {Object} [presolvedDisplayCapture] - Result from ensureDisplayAudio(); skips re-check and modal in getRecordingStream
 * @property {{ userId: string, token?: string }} [azureStreaming] - When set with REACT_APP_ENABLE_STREAMING_TRANSCRIPTION=true, stream PCM to server for live STT (Deepgram preferred, else Azure). Pass only for explicit “live transcribing” flows, not record-only capture.
 */

const defaultOptions = { audioSourceMode: "input", audioInputDeviceId: undefined }

const TAB_FALLBACK_MSG =
  "System audio often fails on Windows. Try this instead:\n\n" +
  "1. Open the audio in a Chrome tab (e.g. YouTube, Spotify Web).\n" +
  "2. Start recording again and choose \"Chrome tab\".\n" +
  "3. Select that tab and check \"Share tab audio\", then Share.\n\n" +
  "Tab audio is more reliable than \"Entire screen\" system audio on many PCs."

const NO_AUDIO_MSG =
  "No audio was captured. Turn ON \"Share system audio\" (Entire screen) or \"Share tab audio\" (tab), then Share. If it keeps failing, use a browser tab (e.g. YouTube) and \"Share tab audio\" instead."

/** @returns {MediaStreamTrack[]} */
function getStreamAudioTracks(stream) {
  const audio = stream.getAudioTracks()
  if (audio.length > 0) return audio
  return stream.getTracks().filter((track) => track.kind === "audio")
}

/** Some browsers add display audio tracks asynchronously after getDisplayMedia. */
const DISPLAY_AUDIO_QUICK_CHECK_MS = 300
const DISPLAY_AUDIO_RESHARE_WAIT_MS = 2000

function waitForDisplayAudioTracks(stream, timeoutMs = DISPLAY_AUDIO_QUICK_CHECK_MS) {
  return new Promise((resolve) => {
    const getAudio = () => getStreamAudioTracks(stream)
    const audio = getAudio()
    if (audio.length > 0) {
      resolve(audio)
      return
    }
    let resolved = false
    const done = (tracks) => {
      if (resolved) return
      resolved = true
      clearTimeout(tid)
      clearInterval(intervalId)
      stream.removeEventListener("addtrack", onAddTrack)
      resolve(tracks)
    }
    const onAddTrack = (e) => {
      if (e.track.kind === "audio") done(getAudio().length ? getAudio() : [e.track])
    }
    const tid = setTimeout(() => done(getAudio()), timeoutMs)
    const intervalId = setInterval(() => {
      const t = getAudio()
      if (t.length > 0) done(t)
    }, 150)
    stream.addEventListener("addtrack", onAddTrack)
  })
}

/** Must be called directly from a user click (before other awaits) so browsers allow getDisplayMedia. */
export async function acquireDisplayMediaStream() {
  if (!navigator.mediaDevices?.getDisplayMedia) {
    throw new Error("This browser does not support capturing computer audio. Try Chrome or Edge.")
  }
  try {
    return await navigator.mediaDevices.getDisplayMedia(buildDisplayMediaOptions())
  } catch (e) {
    const retryWithFallback =
      e?.name === "TypeError" ||
      e?.name === "OverconstrainedError" ||
      (e?.message && /constraint|not supported|invalid/i.test(e.message))
    if (retryWithFallback) {
      try {
        return await navigator.mediaDevices.getDisplayMedia(DISPLAY_MEDIA_OPTIONS_FALLBACK)
      } catch (fallbackErr) {
        if (fallbackErr.name === "NotAllowedError" || fallbackErr.name === "AbortError") {
          throw new Error("Share cancelled.")
        }
        throw fallbackErr
      }
    }
    if (e.name === "NotAllowedError" || e.name === "AbortError") {
      throw new Error("Share cancelled.")
    }
    if (e.name === "NotReadableError" || (e.message && e.message.toLowerCase().includes("audio"))) {
      throw new Error("System audio failed on this device. " + TAB_FALLBACK_MSG)
    }
    throw e
  }
}

export function stopMediaStream(stream) {
  stream?.getTracks?.().forEach((t) => t.stop())
}

export function needsDisplayMediaCapture(audioSourceMode) {
  return audioSourceMode === "output" || audioSourceMode === "both"
}

export function needsMicCapture(audioSourceMode) {
  return audioSourceMode === "input" || audioSourceMode === "both"
}

/** Brief wait after `stop` so the server can flush last `final` segments (keep small to reduce end-of-session latency). */
const AZURE_FINAL_DRAIN_MS = 380
/** Second pass for any finals that arrive just after drain (SOAP / callbacks). */
const AZURE_GRAMMAR_RECHECK_MS = AZURE_FINAL_DRAIN_MS + 120
/** Max wait for server `streaming_transcription:stopped` (and late finals) before proceeding to SOAP. */
const STREAMING_STOP_WAIT_MS = 10000

function downsampleFloat32(input, inputRate, outputRate) {
  if (inputRate === outputRate) return input
  const ratio = inputRate / outputRate
  const outLen = Math.floor(input.length / ratio)
  const out = new Float32Array(outLen)
  for (let i = 0; i < outLen; i++) {
    const idx = Math.min(input.length - 1, Math.floor(i * ratio))
    out[i] = input[idx] || 0
  }
  return out
}

function floatTo16BitPCM(float32Array) {
  const len = float32Array.length
  const out = new ArrayBuffer(len * 2)
  const view = new DataView(out)
  for (let i = 0; i < len; i++) {
    const s = Math.max(-1, Math.min(1, float32Array[i]))
    view.setInt16(i * 2, s < 0 ? s * 0x8000 : s * 0x7fff, true)
  }
  return new Uint8Array(out)
}

function uint8ToBase64(u8) {
  let binary = ""
  const chunk = 0x8000
  for (let i = 0; i < u8.length; i += chunk) {
    binary += String.fromCharCode.apply(null, u8.subarray(i, i + chunk))
  }
  return btoa(binary)
}

async function getMicPermissionState() {
  if (!navigator.permissions?.query) return "unknown"
  try {
    const result = await navigator.permissions.query({ name: "microphone" })
    return result.state
  } catch {
    return "unknown"
  }
}

/**
 * Attempts microphone access via the browser permission dialog.
 * @param {{ audioInputDeviceId?: string }} [options]
 * @returns {Promise<{ granted: boolean, blocked: boolean, notFound: boolean }>}
 */
async function attemptMicAccess(options = {}) {
  const { audioInputDeviceId } = options

  try {
    const stream = await acquireMicrophoneStream(audioInputDeviceId)
    stream.getTracks().forEach((track) => track.stop())
    return { granted: true, blocked: false, notFound: false }
  } catch (err) {
    if (err.name === "NotAllowedError" || err.name === "PermissionDeniedError") {
      const permissionState = await getMicPermissionState()
      return { granted: false, blocked: permissionState === "denied", notFound: false }
    }
    if (err.name === "NotFoundError" || err.name === "DevicesNotFoundError") {
      return { granted: false, blocked: false, notFound: true }
    }
    return { granted: false, blocked: false, notFound: false }
  }
}

function mapAudioInputDevices(devices) {
  return devices
    .filter((d) => d.kind === "audioinput")
    .map((d) => ({
      deviceId: d.deviceId,
      label: d.label || `Microphone ${d.deviceId.slice(0, 8)}`,
      kind: d.kind,
    }))
}

function formatRecordingClock(totalSeconds) {
  const minutes = String(Math.floor(totalSeconds / 60)).padStart(2, "0")
  const seconds = String(totalSeconds % 60).padStart(2, "0")
  return `${minutes}:${seconds}`
}

export const useRecording = () => {
  const [isRecording, setIsRecording] = useState(false)
  const [isPaused, setIsPaused] = useState(false)
  /** Parked across patient switches: transcript/audio kept, hardware released. */
  const [isParked, setIsParked] = useState(false)
  /** @type {'azure' | 'deepgram' | 'browser' | null} */
  const [transcriptionSource, setTranscriptionSource] = useState(null)

  /**
   * High-frequency live UI (transcript / partials / clock) lives outside React
   * state so MedicalScribeApp + SessionManager do not re-render every STT tick.
   */
  const liveUiRef = useRef({
    transcript: "",
    liveTranscript: "",
    recordingTime: "00:00",
  })
  const liveUiListenersRef = useRef(new Set())
  const patchLiveUi = useCallback((patch) => {
    const prev = liveUiRef.current
    let changed = false
    const next = { ...prev }
    for (const key of Object.keys(patch)) {
      if (next[key] !== patch[key]) {
        next[key] = patch[key]
        changed = true
      }
    }
    if (!changed) return
    liveUiRef.current = next
    for (const listener of liveUiListenersRef.current) {
      try {
        listener()
      } catch (_) {
        /* ignore */
      }
    }
  }, [])
  const subscribeLiveUi = useCallback((listener) => {
    if (typeof listener !== "function") return () => {}
    liveUiListenersRef.current.add(listener)
    return () => liveUiListenersRef.current.delete(listener)
  }, [])
  const getLiveUiSnapshot = useCallback(() => liveUiRef.current, [])
  const setTranscript = useCallback(
    (value) => patchLiveUi({ transcript: typeof value === "string" ? value : "" }),
    [patchLiveUi]
  )
  const setLiveTranscript = useCallback(
    (value) =>
      patchLiveUi({ liveTranscript: typeof value === "string" ? value : "" }),
    [patchLiveUi]
  )
  const setRecordingTime = useCallback(
    (value) =>
      patchLiveUi({
        recordingTime: typeof value === "string" ? value : "00:00",
      }),
    [patchLiveUi]
  )

  const isRecordingRef = useRef(false)
  const isPausedRef = useRef(false)
  const isParkedRef = useRef(false)
  const recognitionRef = useRef(null)
  const timerRef = useRef(null)
  const timeCounter = useRef(0)
  const mediaRecorderRef = useRef(null)
  const audioChunksRef = useRef([])
  /** Completed audio blobs from earlier pause/park segments in this capture. */
  const audioSegmentsRef = useRef([])
  const accumulatedTranscriptRef = useRef("")
  const pendingLiveTranscriptRef = useRef("")
  /** Coalesce rapid partials to one paint per frame (smooth + keeps latest text). */
  const livePartialRafRef = useRef(null)
  /** Refs to original streams so we can stop all tracks on stop */
  const streamsToStopRef = useRef([])
  const azureStreamIdRef = useRef(null)
  /** streamId of the in-flight server streaming attempt, set before the start ack arrives. */
  const activeStreamIdRef = useRef(null)
  /** userId/token/locale for the current server stream, so it can be re-established after a drop. */
  const liveStreamingMetaRef = useRef(null)
  /** Guards against showing the same streaming-error toast more than once per stream. */
  const streamErrToastShownRef = useRef(false)
  const azureCleanupRef = useRef(null)
  /** True while Azure Socket.IO streaming is active (used to re-run grammar after drain). */
  const hadAzureStreamingRef = useRef(false)
  const azureDrainTimerRef = useRef(null)
  const audioContextRef = useRef(null)
  const streamingRestartPendingRef = useRef(false)
  const recognitionLangRef = useRef("en-US")
  const onTranscriptUpdateRef = useRef(null)
  const timerPausedRef = useRef(false)
  /** Last start options so resume-after-park can recreate the same capture path. */
  const lastStartOptionsRef = useRef(null)
  const lastOnAudioReadyRef = useRef(null)
  /** Bumped on each new capture so a previous visit's STT cannot write into the open visit. */
  const captureGenerationRef = useRef(0)
  /** Full transcript per capture generation (survives visit switch during Stop drain). */
  const drainTranscriptByGenerationRef = useRef(new Map())
  /** When false, live STT still drains into the capture map but does not paint the UI. */
  const liveUiBoundRef = useRef(true)

  const resetTranscriptBuffers = useCallback(() => {
    accumulatedTranscriptRef.current = ""
    pendingLiveTranscriptRef.current = ""
    setTranscript("")
    setLiveTranscript("")
  }, [])

  const appendFinalTranscript = useCallback((generation, text, joiner = "\n") => {
    const piece = String(text || "").trim()
    if (!piece || generation == null) return
    const prev = drainTranscriptByGenerationRef.current.get(generation) || ""
    const next = prev
      ? `${String(prev).replace(/\s+$/, "")}${joiner}${piece}${joiner === "\n" ? "\n" : ""}`
      : joiner === "\n"
        ? `${piece}\n`
        : `${piece}${joiner}`
    drainTranscriptByGenerationRef.current.set(generation, next)
    if (liveUiBoundRef.current && captureGenerationRef.current === generation) {
      accumulatedTranscriptRef.current = next
      setTranscript(next)
      onTranscriptUpdateRef.current?.(next)
    }
  }, [])

  const getCaptureGeneration = useCallback(() => captureGenerationRef.current, [])

  /** Keep React transcript state and accumulatedTranscriptRef in sync (session load, edits). */
  const syncTranscript = useCallback((text) => {
    const t = typeof text === "string" ? text : ""
    accumulatedTranscriptRef.current = t
    pendingLiveTranscriptRef.current = ""
    setLiveTranscript("")
    setTranscript(t)
    const generation = captureGenerationRef.current
    if (generation && liveUiBoundRef.current) {
      drainTranscriptByGenerationRef.current.set(generation, t)
    }
  }, [])

  const waitForStreamingDrain = useCallback((streamId, hadServerStreaming) => {
    if (!hadServerStreaming) return Promise.resolve()
    return new Promise((resolve) => {
      let settled = false
      const finish = () => {
        if (settled) return
        settled = true
        clearTimeout(timer)
        const socket = getSocket()
        if (socket) socket.off("streaming_transcription:stopped", onStopped)
        resolve()
      }
      const onStopped = (payload) => {
        if (streamId && payload?.streamId !== streamId) return
        finish()
      }
      const socket = getSocket()
      if (socket?.connected) {
        socket.on("streaming_transcription:stopped", onStopped)
      }
      const timer = setTimeout(finish, STREAMING_STOP_WAIT_MS)
    })
  }, [])

  const cancelScheduledLivePartial = useCallback(() => {
    if (livePartialRafRef.current != null) {
      cancelAnimationFrame(livePartialRafRef.current)
      livePartialRafRef.current = null
    }
  }, [])

  const scheduleLivePartial = useCallback((text) => {
    if (!liveUiBoundRef.current) return
    pendingLiveTranscriptRef.current = text
    if (livePartialRafRef.current != null) return
    livePartialRafRef.current = requestAnimationFrame(() => {
      livePartialRafRef.current = null
      if (!liveUiBoundRef.current) return
      setLiveTranscript(pendingLiveTranscriptRef.current)
    })
  }, [])

  /**
   * Clear the visible live transcript when switching visits without killing
   * an in-flight Stop drain for the previous capture.
   */
  const detachLiveTranscriptUi = useCallback(() => {
    liveUiBoundRef.current = false
    cancelScheduledLivePartial()
    pendingLiveTranscriptRef.current = ""
    // Keep accumulatedTranscriptRef so Stop/park can still snapshot this capture.
    setTranscript("")
    setLiveTranscript("")
  }, [cancelScheduledLivePartial])

  /** Drop recording chrome immediately without waiting for MediaRecorder/STT drain. */
  const releaseCaptureUi = useCallback(() => {
    setIsRecording(false)
    setIsPaused(false)
    setIsParked(false)
  }, [])

  const teardownAzureStreaming = useCallback((opts = {}) => {
    const immediate = opts.immediate === true
    const preserveHandlers = opts.preserveHandlers === true
    const finishOnly = opts.finishOnly === true
    const streamId = opts.streamId
    if (typeof azureCleanupRef.current === "function") {
      try {
        azureCleanupRef.current({ immediate, preserveHandlers, finishOnly, streamId })
      } catch (e) {
        console.warn("teardownAzureStreaming", e)
      }
      return
    }
    if (immediate || finishOnly) {
      cancelScheduledLivePartial()
      if (azureDrainTimerRef.current) {
        clearTimeout(azureDrainTimerRef.current)
        azureDrainTimerRef.current = null
      }
      if (!streamId || azureStreamIdRef.current === streamId) {
        azureStreamIdRef.current = null
        hadAzureStreamingRef.current = false
        setTranscriptionSource(null)
        audioContextRef.current = null
      }
    }
  }, [cancelScheduledLivePartial])

  const waitForStreamingStarted = useCallback((socket, streamId, timeoutMs = 12000) => {
    return new Promise((resolve, reject) => {
      const t = setTimeout(
        () => reject(new Error("Timed out waiting for streaming transcription to start")),
        timeoutMs
      )
      const onStarted = (payload) => {
        if (payload?.streamId !== streamId) return
        clearTimeout(t)
        socket.off("streaming_transcription:started", onStarted)
        socket.off("streaming_transcription:error", onWaitErr)
        resolve(payload?.provider === "deepgram" ? "deepgram" : "azure")
      }
      const onWaitErr = (payload) => {
        if (payload?.streamId != null && payload.streamId !== streamId) return
        clearTimeout(t)
        socket.off("streaming_transcription:started", onStarted)
        socket.off("streaming_transcription:error", onWaitErr)
        reject(new Error(payload?.message || "Streaming transcription failed to start"))
      }
      socket.on("streaming_transcription:started", onStarted)
      socket.on("streaming_transcription:error", onWaitErr)
      socket.emit("streaming_transcription:start", { streamId, locale: liveStreamingMetaRef.current?.locale })
    })
  }, [])

  const restartLiveStreamingSession = useCallback(async () => {
    const meta = liveStreamingMetaRef.current
    if (!meta || !isRecordingRef.current || isPausedRef.current) return false
    const socket = getSocket()
    if (!socket?.connected) return false

    const wsReady = await ensureWebSocketReady(meta.userId, meta.token, 15000)
    if (!wsReady) return false

    const oldStreamId = activeStreamIdRef.current
    if (oldStreamId) {
      socket.emit("streaming_transcription:stop", { streamId: oldStreamId })
    }

    const streamId =
      typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID()
        : `st-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
    activeStreamIdRef.current = streamId
    azureStreamIdRef.current = streamId
    streamErrToastShownRef.current = false

    try {
      await waitForStreamingStarted(socket, streamId)
      if (audioContextRef.current?.state === "suspended") {
        await audioContextRef.current.resume()
      }
      toast.success("Live transcription reconnected", { duration: 3000 })
      return true
    } catch (e) {
      console.warn("Failed to restart live streaming session:", e?.message || e)
      toast.error("Could not restore live transcription. Stop and start again.", { duration: 5000 })
      return false
    }
  }, [waitForStreamingStarted])

  const startTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current)
      timerRef.current = null
    }
    timerRef.current = setInterval(() => {
      timeCounter.current++
      setRecordingTime(formatRecordingClock(timeCounter.current))
    }, 1000)
  }, [])

  const stopTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current)
      timerRef.current = null
    }
  }, [])

  const pauseTimer = useCallback(() => {
    timerPausedRef.current = true
    stopTimer()
  }, [stopTimer])

  const resumeTimer = useCallback(() => {
    if (!isRecordingRef.current || !timerPausedRef.current) return
    timerPausedRef.current = false
    if (timerRef.current) return
    timerRef.current = setInterval(() => {
      timeCounter.current++
      setRecordingTime(formatRecordingClock(timeCounter.current))
    }, 1000)
  }, [])

  const stopAllMediaTracks = useCallback(() => {
    if (mediaRecorderRef.current?.stream) {
      mediaRecorderRef.current.stream.getTracks().forEach((t) => t.stop())
    }
    streamsToStopRef.current.forEach((s) => s.getTracks().forEach((t) => t.stop()))
    streamsToStopRef.current = []
  }, [])

  const collectCurrentAudioBlob = useCallback((recorderOverride) => {
    return new Promise((resolve) => {
      const recorder = recorderOverride || mediaRecorderRef.current
      let settled = false
      const finish = (blob) => {
        if (settled) return
        settled = true
        resolve(blob && blob.size > 0 ? blob : null)
      }

      const takeChunks = () => {
        const leftover = (recorder?._vbotChunks || audioChunksRef.current).slice()
        if (!leftover.length) return null
        return new Blob(leftover, {
          type: leftover[0]?.type || recorder?.mimeType || "audio/webm",
        })
      }

      if (!recorder || recorder.state === "inactive") {
        const leftover = takeChunks()
        if (mediaRecorderRef.current === recorder) {
          audioChunksRef.current = []
        }
        finish(leftover)
        return
      }

      const onStop = () => {
        clearTimeout(tid)
        recorder.removeEventListener("stop", onStop)
        const mime = recorder.mimeType || "audio/webm"
        const chunks = (recorder._vbotChunks || audioChunksRef.current).slice()
        const blob = chunks.length > 0 ? new Blob(chunks, { type: mime }) : null
        if (mediaRecorderRef.current === recorder) {
          audioChunksRef.current = []
          stopAllMediaTracks()
          mediaRecorderRef.current = null
        }
        finish(blob)
      }

      recorder.addEventListener("stop", onStop)
      const tid = setTimeout(() => {
        recorder.removeEventListener("stop", onStop)
        if (mediaRecorderRef.current === recorder) {
          audioChunksRef.current = []
          stopAllMediaTracks()
          mediaRecorderRef.current = null
        }
        finish(takeChunks())
      }, 2000)

      try {
        if (recorder.state === "paused") {
          recorder.resume()
        }
      } catch (_) {}

      try {
        recorder.requestData?.()
      } catch (_) {}

      try {
        recorder.stop()
      } catch (_) {
        recorder.removeEventListener("stop", onStop)
        clearTimeout(tid)
        if (mediaRecorderRef.current === recorder) {
          stopAllMediaTracks()
          mediaRecorderRef.current = null
        }
        finish(takeChunks())
      }
    })
  }, [stopAllMediaTracks])

  const buildMergedAudioFile = useCallback((segments, prefix = "live_recording") => {
    const usable = (segments || []).filter((b) => b && b.size > 0)
    if (usable.length === 0) return null
    const mime = usable[0].type || "audio/webm"
    const merged = usable.length === 1 ? usable[0] : new Blob(usable, { type: mime })
    const ext = mime.includes("webm") ? "webm" : "wav"
    return new File([merged], `${prefix}_${Date.now()}.${ext}`, { type: mime })
  }, [])

  const [micModalOpen, setMicModalOpen] = useState(false)
  const [micModalBlocked, setMicModalBlocked] = useState(false)
  const [micModalRequesting, setMicModalRequesting] = useState(false)
  const micPermissionResolverRef = useRef(null)
  const micPermissionDeviceIdRef = useRef(undefined)

  const finishMicPermissionRequest = useCallback((granted) => {
    setMicModalOpen(false)
    setMicModalRequesting(false)
    setMicModalBlocked(false)
    const resolver = micPermissionResolverRef.current
    micPermissionResolverRef.current = null
    micPermissionDeviceIdRef.current = undefined
    resolver?.(granted)
  }, [])

  const handleMicModalEnable = useCallback(async () => {
    setMicModalRequesting(true)
    const result = await attemptMicAccess({
      audioInputDeviceId: micPermissionDeviceIdRef.current,
    })

    if (result.granted) {
      finishMicPermissionRequest(true)
      return
    }

    if (result.notFound) {
      toast.error("No microphone found. Connect a microphone and try again.")
      finishMicPermissionRequest(false)
      return
    }

    if (result.blocked) {
      setMicModalRequesting(false)
      setMicModalBlocked(true)
      return
    }

    setMicModalRequesting(false)
    toast(
      "Microphone permission is required. Click Allow when your browser asks for access.",
      { icon: "🎤", duration: 5000 }
    )
  }, [finishMicPermissionRequest])

  const handleMicModalClose = useCallback(() => {
    finishMicPermissionRequest(false)
  }, [finishMicPermissionRequest])

  const openMicPermissionModal = useCallback((audioInputDeviceId, { blocked = false } = {}) => {
    return new Promise((resolve) => {
      micPermissionResolverRef.current = resolve
      micPermissionDeviceIdRef.current = audioInputDeviceId
      setMicModalBlocked(blocked)
      setMicModalRequesting(false)
      setMicModalOpen(true)
    })
  }, [])

  const checkMicPermission = useCallback(async (audioInputDeviceId) => {
    const permissionState = await getMicPermissionState()
    if (permissionState === "granted") {
      const result = await attemptMicAccess({ audioInputDeviceId })
      if (result.granted) return true
      if (result.notFound) {
        toast.error("No microphone found. Connect a microphone and try again.")
        return false
      }
    }

    if (permissionState === "denied") {
      return openMicPermissionModal(audioInputDeviceId, { blocked: true })
    }

    const directResult = await attemptMicAccess({ audioInputDeviceId })
    if (directResult.granted) return true
    if (directResult.notFound) {
      toast.error("No microphone found. Connect a microphone and try again.")
      return false
    }

    return openMicPermissionModal(audioInputDeviceId)
  }, [openMicPermissionModal])

  /** Returns list of audio input devices for dropdown */
  const getAudioInputDevices = useCallback(async ({ requestPermission = false } = {}) => {
    try {
      const permissionState = await getMicPermissionState()

      if (permissionState === "granted") {
        const devices = await navigator.mediaDevices.enumerateDevices()
        return mapAudioInputDevices(devices)
      }

      if (!requestPermission) return []

      const granted = await checkMicPermission()
      if (!granted) return []

      const devices = await navigator.mediaDevices.enumerateDevices()
      return mapAudioInputDevices(devices)
    } catch (err) {
      console.error("Failed to enumerate devices:", err)
      return []
    }
  }, [checkMicPermission])

  const [displayAudioModalOpen, setDisplayAudioModalOpen] = useState(false)
  const [displayAudioModalAllowMicOnly, setDisplayAudioModalAllowMicOnly] = useState(true)
  const [displayAudioModalAllowContinueAnyway, setDisplayAudioModalAllowContinueAnyway] =
    useState(false)
  const [displayAudioModalResharing, setDisplayAudioModalResharing] = useState(false)
  const [displayAudioModalRetryFailed, setDisplayAudioModalRetryFailed] = useState(false)
  const displayAudioResolverRef = useRef(null)
  const displayAudioPendingStreamRef = useRef(null)

  const finishDisplayAudioRequest = useCallback((result) => {
    setDisplayAudioModalOpen(false)
    setDisplayAudioModalResharing(false)
    setDisplayAudioModalRetryFailed(false)
    setDisplayAudioModalAllowContinueAnyway(false)
    const resolver = displayAudioResolverRef.current
    displayAudioResolverRef.current = null
    resolver?.(result)
  }, [])

  const openDisplayAudioModal = useCallback(
    ({ allowMicOnly = true, allowContinueAnyway = false, pendingStream = null } = {}) =>
      new Promise((resolve) => {
        displayAudioResolverRef.current = resolve
        displayAudioPendingStreamRef.current = pendingStream
        flushSync(() => {
          setDisplayAudioModalAllowMicOnly(allowMicOnly)
          setDisplayAudioModalAllowContinueAnyway(allowContinueAnyway)
          setDisplayAudioModalRetryFailed(false)
          setDisplayAudioModalResharing(false)
          setDisplayAudioModalOpen(true)
        })
      }),
    []
  )

  const resolveDisplayCapture = useCallback(
    async (
      displayStream,
      { allowMicOnly, allowContinueAnyway, audioWaitMs = DISPLAY_AUDIO_QUICK_CHECK_MS } = {}
    ) => {
      const displayAudioTracks = await waitForDisplayAudioTracks(displayStream, audioWaitMs)
      if (displayAudioTracks.length > 0) {
        return {
          displayStream,
          displayAudioTracks,
          useVideoFallback: false,
          usedMicOnly: false,
          cancelled: false,
        }
      }

      const hasVideo = displayStream.getVideoTracks().length > 0
      const result = await openDisplayAudioModal({
        allowMicOnly,
        allowContinueAnyway: allowContinueAnyway && hasVideo,
        pendingStream: displayStream,
      })

      if (result.action === "cancel") {
        stopMediaStream(displayStream)
        return { cancelled: true }
      }
      if (result.action === "mic-only") {
        stopMediaStream(displayStream)
        return { usedMicOnly: true, cancelled: false }
      }
      if (result.action === "continue") {
        return {
          displayStream: result.displayStream,
          displayAudioTracks: [],
          useVideoFallback: true,
          usedMicOnly: false,
          cancelled: false,
        }
      }
      return {
        displayStream: result.displayStream,
        displayAudioTracks: result.displayAudioTracks,
        useVideoFallback: false,
        usedMicOnly: false,
        cancelled: false,
      }
    },
    [openDisplayAudioModal]
  )

  /** Quick audio check + modal right after screen share (before other slow awaits). */
  const ensureDisplayAudio = useCallback(
    async (displayStream, audioSourceMode) => {
      if (!displayStream || !needsDisplayMediaCapture(audioSourceMode)) return null
      return resolveDisplayCapture(displayStream, {
        allowMicOnly: audioSourceMode === "both",
        allowContinueAnyway: audioSourceMode === "output",
        audioWaitMs: DISPLAY_AUDIO_QUICK_CHECK_MS,
      })
    },
    [resolveDisplayCapture]
  )

  const handleDisplayAudioReshare = useCallback(async () => {
    setDisplayAudioModalResharing(true)
    setDisplayAudioModalRetryFailed(false)
    stopMediaStream(displayAudioPendingStreamRef.current)
    displayAudioPendingStreamRef.current = null

    try {
      const stream = await acquireDisplayMediaStream()
      const tracks = await waitForDisplayAudioTracks(stream, DISPLAY_AUDIO_RESHARE_WAIT_MS)
      if (tracks.length === 0) {
        stopMediaStream(stream)
        setDisplayAudioModalRetryFailed(true)
        setDisplayAudioModalResharing(false)
        return
      }
      finishDisplayAudioRequest({
        action: "reshared",
        displayStream: stream,
        displayAudioTracks: tracks,
      })
    } catch (err) {
      setDisplayAudioModalResharing(false)
      if (err?.message === "Share cancelled.") return
      toast.error(err?.message || "Could not share screen.")
    }
  }, [finishDisplayAudioRequest])

  const handleDisplayAudioMicOnly = useCallback(() => {
    stopMediaStream(displayAudioPendingStreamRef.current)
    displayAudioPendingStreamRef.current = null
    finishDisplayAudioRequest({ action: "mic-only" })
  }, [finishDisplayAudioRequest])

  const handleDisplayAudioContinue = useCallback(() => {
    const stream = displayAudioPendingStreamRef.current
    displayAudioPendingStreamRef.current = null
    finishDisplayAudioRequest({ action: "continue", displayStream: stream })
  }, [finishDisplayAudioRequest])

  const handleDisplayAudioCancel = useCallback(() => {
    stopMediaStream(displayAudioPendingStreamRef.current)
    displayAudioPendingStreamRef.current = null
    finishDisplayAudioRequest({ action: "cancel" })
  }, [finishDisplayAudioRequest])

  /**
   * @returns {Promise<MediaStream>}
   */
  const getRecordingStream = useCallback(async (options = {}) => {
    const {
      audioSourceMode = "input",
      audioInputDeviceId,
      preacquiredDisplayStream = null,
      presolvedDisplayCapture = null,
    } = { ...defaultOptions, ...options }
    const streamsToStop = []
    streamsToStopRef.current = streamsToStop

    if (audioSourceMode === "input") {
      const stream = await acquireMicrophoneStream(audioInputDeviceId)
      streamsToStop.push(stream)
      return stream
    }

    // User already chose "Continue with microphone only" in the no-audio modal.
    // Never call getDisplayMedia again — user-gesture is gone after the modal await.
    if (presolvedDisplayCapture?.usedMicOnly) {
      const micStream = await acquireMicrophoneStream(audioInputDeviceId)
      streamsToStop.push(micStream)
      return micStream
    }

    if (presolvedDisplayCapture?.cancelled) {
      throw new Error(
        audioSourceMode === "output"
          ? NO_AUDIO_MSG + "\n\n" + TAB_FALLBACK_MSG
          : "Share cancelled."
      )
    }

    const resolveDisplayAudio = async (displayStream, { allowMicOnly, allowContinueAnyway }) => {
      if (presolvedDisplayCapture) {
        return presolvedDisplayCapture
      }
      return resolveDisplayCapture(displayStream, {
        allowMicOnly,
        allowContinueAnyway,
        audioWaitMs: DISPLAY_AUDIO_QUICK_CHECK_MS,
      })
    }

    /** Only open the share picker from a fresh user click — never after the audio modal. */
    const getOrAcquireDisplayStream = async () => {
      if (preacquiredDisplayStream) return preacquiredDisplayStream
      if (presolvedDisplayCapture?.displayStream) return presolvedDisplayCapture.displayStream
      if (presolvedDisplayCapture) {
        // Already resolved (e.g. continue-anyway / reshare with tracks) — no new picker.
        return null
      }
      return acquireDisplayMediaStream()
    }

    if (audioSourceMode === "output") {
      if (!navigator.mediaDevices.getDisplayMedia) {
        throw new Error("This browser does not support capturing computer audio. Try Chrome or Edge.")
      }

      const displayStream = await getOrAcquireDisplayStream()
      const resolved = await resolveDisplayAudio(displayStream, {
        allowMicOnly: false,
        allowContinueAnyway: true,
      })

      if (resolved.usedMicOnly) {
        throw new Error(NO_AUDIO_MSG + "\n\n" + TAB_FALLBACK_MSG)
      }

      if (resolved.displayStream) {
        streamsToStop.push(resolved.displayStream)
      }
      if (resolved.displayAudioTracks?.length > 0) {
        return new MediaStream(resolved.displayAudioTracks)
      }
      if (resolved.useVideoFallback && resolved.displayStream) {
        console.warn("No audio tracks reported; recording display stream in case audio is muxed.")
        return resolved.displayStream
      }
      if (resolved.displayStream) {
        stopMediaStream(resolved.displayStream)
        streamsToStop.pop()
      }
      throw new Error(NO_AUDIO_MSG + "\n\n" + TAB_FALLBACK_MSG)
    }

    if (audioSourceMode === "both") {
      if (!navigator.mediaDevices.getDisplayMedia) {
        throw new Error("This browser does not support capturing computer audio. Try Chrome or Edge.")
      }

      const displayStream = await getOrAcquireDisplayStream()
      const resolved = await resolveDisplayAudio(displayStream, {
        allowMicOnly: true,
        allowContinueAnyway: false,
      })

      if (resolved.cancelled) {
        throw new Error("Share cancelled.")
      }

      if (resolved.usedMicOnly || !resolved.displayAudioTracks?.length) {
        const micStream = await acquireMicrophoneStream(audioInputDeviceId)
        streamsToStop.push(micStream)
        return micStream
      }

      const micStream = await acquireMicrophoneStream(audioInputDeviceId)
      if (resolved.displayStream) {
        streamsToStop.push(resolved.displayStream)
      }
      streamsToStop.push(micStream)

      const AudioContextClass = window.AudioContext || window.webkitAudioContext
      const ctx = new AudioContextClass()
      if (ctx.state === "suspended") {
        try {
          await ctx.resume()
        } catch (_) {}
      }
      const destination = ctx.createMediaStreamDestination()

      const micSource = ctx.createMediaStreamSource(micStream)
      const displaySource = ctx.createMediaStreamSource(
        new MediaStream(resolved.displayAudioTracks)
      )
      micSource.connect(destination)
      displaySource.connect(destination)

      return destination.stream
    }

    throw new Error("Invalid audioSourceMode")
  }, [resolveDisplayCapture])

  const startBrowserRecognition = useCallback(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition
    if (!SpeechRecognition || !isRecordingRef.current || isPausedRef.current) return
    const generation = captureGenerationRef.current

    const recognition = new SpeechRecognition()
    recognition.continuous = false
    recognition.interimResults = true
    recognition.lang = recognitionLangRef.current
    recognition.maxAlternatives = 3

    recognition.onresult = (event) => {
      let interimTranscript = ""
      let gotFinal = false
      for (let i = event.resultIndex; i < event.results.length; ++i) {
        const result = event.results[i]
        const text = result[0].transcript
        if (result.isFinal) {
          gotFinal = true
          appendFinalTranscript(generation, text, " ")
        } else {
          interimTranscript += text
        }
      }
      if (gotFinal) {
        cancelScheduledLivePartial()
        pendingLiveTranscriptRef.current = ""
        setLiveTranscript("")
      } else {
        scheduleLivePartial(interimTranscript)
      }
    }

    recognition.onerror = (event) => {
      console.error("Speech recognition error:", event.error)
    }

    let restartTimeout
    recognition.onend = () => {
      clearTimeout(restartTimeout)
      if (isRecordingRef.current && !isPausedRef.current) {
        restartTimeout = setTimeout(() => {
          if (isRecordingRef.current && !isPausedRef.current) {
            try {
              recognition.start()
            } catch (_) {}
          }
        }, 0)
      }
    }

    recognition.start()
    recognitionRef.current = recognition
    setTranscriptionSource("browser")
  }, [cancelScheduledLivePartial, scheduleLivePartial, appendFinalTranscript])

  const stopBrowserRecognition = useCallback(() => {
    if (!recognitionRef.current) return
    recognitionRef.current.onend = () => {
      recognitionRef.current = null
    }
    try {
      recognitionRef.current.stop()
    } catch (_) {}
  }, [])

  const startRecording = useCallback(
    async (onTranscriptUpdate, onAudioReady, lang = "en-US", options = {}) => {
      const opts = { ...defaultOptions, ...options }
      const preserveTranscript = opts.preserveTranscript === true
      const needsMic = opts.audioSourceMode === "input" || opts.audioSourceMode === "both"
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition
      const streamingFlag = process.env.REACT_APP_ENABLE_STREAMING_TRANSCRIPTION
      const serverStreamingEnabled =
        streamingFlag == null || String(streamingFlag).trim() === ""
          ? true
          : String(streamingFlag).toLowerCase() === "true"
      const wantServerStreaming =
        serverStreamingEnabled && opts.azureStreaming?.userId

      if (needsMic && !SpeechRecognition && !wantServerStreaming) {
        alert("Web Speech API is not supported in this browser.")
        return
      }

      try {
        liveUiBoundRef.current = true
        captureGenerationRef.current += 1
        const generation = captureGenerationRef.current
        cancelScheduledLivePartial()
        teardownAzureStreaming({ immediate: true })
        if (!preserveTranscript) {
          resetTranscriptBuffers()
          audioSegmentsRef.current = []
          timeCounter.current = 0
          setRecordingTime("00:00")
          drainTranscriptByGenerationRef.current.set(generation, "")
        } else if (typeof opts.resumeElapsed === "number" && opts.resumeElapsed >= 0) {
          timeCounter.current = opts.resumeElapsed
          setRecordingTime(formatRecordingClock(timeCounter.current))
          drainTranscriptByGenerationRef.current.set(
            generation,
            accumulatedTranscriptRef.current || ""
          )
        } else {
          drainTranscriptByGenerationRef.current.set(
            generation,
            accumulatedTranscriptRef.current || ""
          )
        }
        timerPausedRef.current = false
        recognitionLangRef.current = lang
        onTranscriptUpdateRef.current = onTranscriptUpdate
        lastOnAudioReadyRef.current = onAudioReady || null
        lastStartOptionsRef.current = {
          lang,
          audioSourceMode: opts.audioSourceMode,
          audioInputDeviceId: opts.audioInputDeviceId,
          azureStreaming: opts.azureStreaming || null,
        }

        // Warm / wait for WS in parallel with mic/display stream acquisition so
        // live capture UI is not blocked behind a serial handshake (especially
        // after Recording Options → screen-share prompts).
        const streamingUserId = wantServerStreaming
          ? String(opts.azureStreaming.userId)
          : null
        const streamingToken = wantServerStreaming
          ? opts.azureStreaming.token || ""
          : ""
        const wsReadyPromise = wantServerStreaming
          ? ensureWebSocketReady(streamingUserId, streamingToken, 15000)
          : Promise.resolve(false)

        const [stream, wsReady] = await Promise.all([
          getRecordingStream(opts),
          wsReadyPromise,
        ])
        isRecordingRef.current = true
        isPausedRef.current = false

        stream.getAudioTracks().forEach((track) => {
          track.onended = () => {
            if (isRecordingRef.current) {
              toast.error("Microphone disconnected. Check your audio device.", { duration: 6000 })
            }
          }
        })

        const mediaRecorder = new MediaRecorder(stream)
        mediaRecorderRef.current = mediaRecorder
        const chunksForThisRecorder = []
        audioChunksRef.current = chunksForThisRecorder
        mediaRecorder._vbotChunks = chunksForThisRecorder

        mediaRecorder.ondataavailable = (event) => {
          if (event.data.size > 0) chunksForThisRecorder.push(event.data)
        }

        mediaRecorder.onstop = () => {
          const mime = mediaRecorder.mimeType || "audio/webm"
          const audioBlob = new Blob(audioChunksRef.current, { type: mime })
          const audioUrl = URL.createObjectURL(audioBlob)
          if (onAudioReady) onAudioReady(audioUrl)
          streamsToStopRef.current.forEach((s) => s.getTracks().forEach((t) => t.stop()))
          streamsToStopRef.current = []
        }

        // timeslice 1s so we get data regularly (helps some browsers with audio-only capture)
        try {
          mediaRecorder.start(1000)
        } catch (_) {
          mediaRecorder.start()
        }

        // Paint Recording… / timer immediately — do not wait for streaming:started.
        isRecordingRef.current = true
        isPausedRef.current = false
        isParkedRef.current = false
        setIsRecording(true)
        setIsPaused(false)
        setIsParked(false)
        setLiveTranscript("")
        startTimer()

        let serverStreamingActive = false

        if (wantServerStreaming) {
          const userId = streamingUserId
          const token = streamingToken
          let socket = wsReady ? getSocket() : null
          if (!socket?.connected) {
            const readyAgain = await ensureWebSocketReady(userId, token, 8000)
            socket = readyAgain ? getSocket() : null
          }
          if (socket?.connected && isRecordingRef.current && captureGenerationRef.current === generation) {
            /** Stays true until sockets are removed so late `final` events still merge after Stop. */
            let azureHandlersActive = true
            const streamId =
              typeof crypto !== "undefined" && crypto.randomUUID
                ? crypto.randomUUID()
                : `st-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
            activeStreamIdRef.current = streamId
            liveStreamingMetaRef.current = { userId, token, locale: lang }
            streamErrToastShownRef.current = false
            // Optimistic label while server ack is in flight.
            setTranscriptionSource("azure")
            try {
              let serverStreamProvider = "azure"
              serverStreamProvider = await waitForStreamingStarted(socket, streamId)

              if (!isRecordingRef.current || captureGenerationRef.current !== generation) {
                return
              }

              if (!isRecordingRef.current || captureGenerationRef.current !== generation) {
                return
              }

              const onPartial = (payload) => {
                if (payload?.streamId !== streamId || !azureHandlersActive) return
                if (
                  !liveUiBoundRef.current ||
                  captureGenerationRef.current !== generation
                ) {
                  return
                }
                const text = (payload.text || "").trim()
                if (!text) return
                scheduleLivePartial(text)
              }

              const onFinal = (payload) => {
                if (payload?.streamId !== activeStreamIdRef.current || !azureHandlersActive) return
                const text = (payload.text || "").trim()
                if (!text) return
                cancelScheduledLivePartial()
                pendingLiveTranscriptRef.current = ""
                if (
                  liveUiBoundRef.current &&
                  captureGenerationRef.current === generation
                ) {
                  setLiveTranscript("")
                }
                appendFinalTranscript(generation, text, "\n")
              }

              const onStreamErr = (payload) => {
                if (payload?.streamId !== activeStreamIdRef.current) return
                console.error("streaming_transcription:", payload?.message)
                if (!streamErrToastShownRef.current) {
                  streamErrToastShownRef.current = true
                  toast.error(payload?.message || "Live transcription error", { duration: 5000 })
                }
              }

              socket.on("streaming_transcription:partial", onPartial)
              socket.on("streaming_transcription:final", onFinal)
              socket.on("streaming_transcription:error", onStreamErr)

              const onSocketDisconnect = () => {
                if (isRecordingRef.current && liveStreamingMetaRef.current && azureStreamIdRef.current) {
                  streamingRestartPendingRef.current = true
                  toast("Live transcription paused — reconnecting…", { icon: "🔌", duration: 5000 })
                }
              }
              const onSocketReconnect = () => {
                if (!streamingRestartPendingRef.current || !isRecordingRef.current) return
                streamingRestartPendingRef.current = false
                void restartLiveStreamingSession()
              }
              socket.on("disconnect", onSocketDisconnect)
              socket.on("connect", onSocketReconnect)

              const AudioContextClass = window.AudioContext || window.webkitAudioContext
              const audioContext = new AudioContextClass()
              audioContextRef.current = audioContext
              if (audioContext.state === "suspended") {
                await audioContext.resume()
              }
              const source = audioContext.createMediaStreamSource(stream)
              const processor = audioContext.createScriptProcessor(2048, 1, 1)
              const mute = audioContext.createGain()
              mute.gain.value = 0

              azureStreamIdRef.current = streamId

              processor.onaudioprocess = (e) => {
                if (!isRecordingRef.current || !azureStreamIdRef.current || isPausedRef.current) return
                const inputData = e.inputBuffer.getChannelData(0)
                if (!inputData?.length) return
                const down = downsampleFloat32(inputData, audioContext.sampleRate, 16000)
                const pcm = floatTo16BitPCM(down)
                const pcmBase64 = uint8ToBase64(pcm)
                const s = getSocket()
                if (s?.connected) {
                  s.emit("streaming_transcription:audio", {
                    streamId: azureStreamIdRef.current,
                    pcmBase64,
                  })
                } else if (!streamingRestartPendingRef.current) {
                  streamingRestartPendingRef.current = true
                }
              }

              source.connect(processor)
              processor.connect(mute)
              mute.connect(audioContext.destination)

              const thisCleanup = { fn: null }
              const finishAzureTeardown = () => {
                cancelScheduledLivePartial()
                if (azureDrainTimerRef.current) {
                  clearTimeout(azureDrainTimerRef.current)
                  azureDrainTimerRef.current = null
                }
                azureHandlersActive = false
                socket.off("streaming_transcription:partial", onPartial)
                socket.off("streaming_transcription:final", onFinal)
                socket.off("streaming_transcription:error", onStreamErr)
                socket.off("disconnect", onSocketDisconnect)
                socket.off("connect", onSocketReconnect)
                try {
                  void audioContext.close()
                } catch (_) {}
                if (azureCleanupRef.current === thisCleanup.fn) {
                  azureCleanupRef.current = null
                }
                if (azureStreamIdRef.current === streamId) {
                  azureStreamIdRef.current = null
                  hadAzureStreamingRef.current = false
                  setTranscriptionSource(null)
                }
              }

              thisCleanup.fn = ({
                immediate = false,
                preserveHandlers = false,
                finishOnly = false,
                streamId: targetStreamId,
              } = {}) => {
                if (targetStreamId && targetStreamId !== streamId) return
                if (finishOnly) {
                  finishAzureTeardown()
                  return
                }
                if (azureDrainTimerRef.current) {
                  clearTimeout(azureDrainTimerRef.current)
                  azureDrainTimerRef.current = null
                }
                // Stop sending PCM before asking the server to flush finals.
                if (azureStreamIdRef.current === streamId) {
                  azureStreamIdRef.current = null
                }
                try {
                  processor.disconnect()
                  source.disconnect()
                  mute.disconnect()
                } catch (_) {}
                const s = getSocket()
                if (s?.connected && streamId) {
                  s.emit("streaming_transcription:stop", { streamId })
                }
                if (preserveHandlers) {
                  return
                }
                if (immediate) {
                  finishAzureTeardown()
                } else {
                  azureDrainTimerRef.current = setTimeout(finishAzureTeardown, AZURE_FINAL_DRAIN_MS)
                }
              }
              azureCleanupRef.current = thisCleanup.fn

              hadAzureStreamingRef.current = true
              serverStreamingActive = true
              setTranscriptionSource(serverStreamProvider)
            } catch (e) {
              console.warn("Server streaming transcription unavailable, falling back if possible:", e?.message || e)
              setTranscriptionSource(null)
            }
          }
        }

        if (!isRecordingRef.current || captureGenerationRef.current !== generation) {
          return
        }

        if (needsMic && !serverStreamingActive) {
          await new Promise((res) => setTimeout(res, 0))
          startBrowserRecognition()
        } else if (!needsMic) {
          recognitionRef.current = null
          if (!serverStreamingActive) setTranscriptionSource(null)
        } else {
          recognitionRef.current = null
        }
      } catch (err) {
        console.error("Error starting recording:", err)
        isRecordingRef.current = false
        if (err.name === "NotAllowedError" || err.name === "PermissionDeniedError") {
          toast(
            "Microphone permission is required. Click Allow when your browser asks for access.",
            { icon: "🎤", duration: 5000 }
          )
        } else if (err.name === "NotFoundError") {
          toast.error("No microphone found. Connect a microphone and try again.")
        } else {
          toast.error(err.message || "Failed to start recording.")
        }
      }
    },
    [startTimer, getRecordingStream, teardownAzureStreaming, scheduleLivePartial, cancelScheduledLivePartial, resetTranscriptBuffers, startBrowserRecognition, appendFinalTranscript]
  )

  const flushPendingLiveLineToAccumulated = useCallback((generationOverride) => {
    const pending = (pendingLiveTranscriptRef.current || "").trim()
    if (!pending) return
    pendingLiveTranscriptRef.current = ""
    const generation =
      generationOverride != null
        ? generationOverride
        : captureGenerationRef.current
    appendFinalTranscript(generation, pending, "\n")
  }, [appendFinalTranscript])

  const stopRecording = useCallback(
    (onFinalTranscript, onAudioFileReady) => {
      cancelScheduledLivePartial()
      const hadAzureStreaming = hadAzureStreamingRef.current
      teardownAzureStreaming({ immediate: false })
      flushPendingLiveLineToAccumulated()

      if (recognitionRef.current) {
        stopBrowserRecognition()
      }

      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
        mediaRecorderRef.current.onstop = () => {
          const mime = mediaRecorderRef.current?.mimeType || "audio/webm"
          const currentBlob = new Blob(audioChunksRef.current, { type: mime })
          audioChunksRef.current = []
          const segments = [...audioSegmentsRef.current]
          if (currentBlob.size > 0) segments.push(currentBlob)
          audioSegmentsRef.current = []
          const file = buildMergedAudioFile(segments, "recording")
          const url = file ? URL.createObjectURL(file) : null
          if (onAudioFileReady && file) onAudioFileReady(file, url)
          stopAllMediaTracks()
          mediaRecorderRef.current = null
        }
        try {
          if (mediaRecorderRef.current.state === "paused") mediaRecorderRef.current.resume()
        } catch (_) {}
        mediaRecorderRef.current.stop()
      } else if (audioSegmentsRef.current.length > 0) {
        const file = buildMergedAudioFile(audioSegmentsRef.current, "recording")
        audioSegmentsRef.current = []
        const url = file ? URL.createObjectURL(file) : null
        if (onAudioFileReady && file) onAudioFileReady(file, url)
        stopAllMediaTracks()
      }

      stopTimer()
      timerPausedRef.current = false
      setIsRecording(false)
      setIsPaused(false)
      setIsParked(false)
      isPausedRef.current = false
      isParkedRef.current = false
      setLiveTranscript("")
      pendingLiveTranscriptRef.current = ""
      isRecordingRef.current = false

      const notifyFinalTranscript = () => {
        const finalText = (accumulatedTranscriptRef.current || "").trim()
        if (finalText && onFinalTranscript) {
          onFinalTranscript(accumulatedTranscriptRef.current)
        }
      }
      notifyFinalTranscript()
      if (hadAzureStreaming) {
        setTimeout(notifyFinalTranscript, AZURE_GRAMMAR_RECHECK_MS)
      }
    },
    [
      stopTimer,
      teardownAzureStreaming,
      flushPendingLiveLineToAccumulated,
      cancelScheduledLivePartial,
      stopBrowserRecognition,
      buildMergedAudioFile,
      stopAllMediaTracks,
    ]
  )

  const stopTranscribing = useCallback(
    async (onFinalTranscript, onAudioFileReady, options = {}) => {
      const generationAtStop =
        options.generation != null
          ? options.generation
          : captureGenerationRef.current
      const streamIdForDrain =
        options.streamId !== undefined
          ? options.streamId
          : azureStreamIdRef.current
      const recorderAtStop = mediaRecorderRef.current
      const fromUi = accumulatedTranscriptRef.current || ""
      const fromDrain = drainTranscriptByGenerationRef.current.get(generationAtStop) || ""
      if (fromUi.length >= fromDrain.length) {
        drainTranscriptByGenerationRef.current.set(generationAtStop, fromUi)
      }

      // Drop recording/parked UI immediately so switching visits during Stop
      // is not treated as an in-progress capture to park.
      setIsRecording(false)
      setIsPaused(false)
      setIsParked(false)
      isPausedRef.current = false
      isParkedRef.current = false
      isRecordingRef.current = false

      cancelScheduledLivePartial()
      const hadServerStreaming = hadAzureStreamingRef.current
      const stillThisCapture = captureGenerationRef.current === generationAtStop
      // Stop sending PCM and ask the server to flush, but keep `final` handlers
      // until drain completes. Uploading audio first used to drop late finals.
      teardownAzureStreaming({
        preserveHandlers: true,
        streamId: streamIdForDrain || undefined,
      })
      flushPendingLiveLineToAccumulated(generationAtStop)

      if (recognitionRef.current) {
        stopBrowserRecognition()
      }

      let currentBlob = null
      let file = null
      let url = null
      let priorSegmentCount = audioSegmentsRef.current.length
      let totalSegments = priorSegmentCount

      if (stillThisCapture && captureGenerationRef.current === generationAtStop) {
        currentBlob = await collectCurrentAudioBlob(recorderAtStop)
        if (captureGenerationRef.current === generationAtStop) {
          priorSegmentCount = audioSegmentsRef.current.length
          if (currentBlob) {
            audioSegmentsRef.current.push(currentBlob)
          }
          file = buildMergedAudioFile(audioSegmentsRef.current, "live_recording")
          totalSegments = audioSegmentsRef.current.length
          audioSegmentsRef.current = []
          url = file ? URL.createObjectURL(file) : null
        }
      }

      await waitForStreamingDrain(streamIdForDrain, hadServerStreaming)
      if (captureGenerationRef.current === generationAtStop) {
        flushPendingLiveLineToAccumulated(generationAtStop)
      }
      teardownAzureStreaming({
        finishOnly: true,
        streamId: streamIdForDrain || undefined,
      })

      if (
        stillThisCapture &&
        captureGenerationRef.current === generationAtStop &&
        onAudioFileReady
      ) {
        if (file) {
          await onAudioFileReady(file, url, {
            finalSegment: currentBlob,
            priorSegmentCount,
            totalSegments,
          })
        } else if (currentBlob) {
          const mime = currentBlob.type || "audio/webm"
          const single = new File([currentBlob], `live_recording_${Date.now()}.webm`, { type: mime })
          await onAudioFileReady(single, URL.createObjectURL(single), {
            finalSegment: currentBlob,
            priorSegmentCount,
            totalSegments: priorSegmentCount + 1,
          })
        }
      }

      if (captureGenerationRef.current === generationAtStop) {
        stopTimer()
        timerPausedRef.current = false
        setIsRecording(false)
        setIsPaused(false)
        setIsParked(false)
        isPausedRef.current = false
        isParkedRef.current = false
        if (liveUiBoundRef.current) {
          setLiveTranscript("")
        }
        isRecordingRef.current = false
        pendingLiveTranscriptRef.current = ""
      }

      const finalText = (
        drainTranscriptByGenerationRef.current.get(generationAtStop) ||
        (captureGenerationRef.current === generationAtStop
          ? accumulatedTranscriptRef.current
          : "") ||
        ""
      ).trim()
      if (finalText && onFinalTranscript) {
        onFinalTranscript(finalText)
      }
      return finalText
    },
    [
      stopTimer,
      teardownAzureStreaming,
      flushPendingLiveLineToAccumulated,
      cancelScheduledLivePartial,
      waitForStreamingDrain,
      stopBrowserRecognition,
      collectCurrentAudioBlob,
      buildMergedAudioFile,
    ]
  )

  /**
   * Release mic/stream while keeping transcript + audio segments for a later resume
   * (e.g. switch to another patient chart while paused).
   * Critical path is fast: collect blob + release hardware; optional short drain.
   */
  const parkRecording = useCallback(async () => {
    if (!isRecordingRef.current && !isParkedRef.current) return null

    const generationAtPark = captureGenerationRef.current
    const recorderAtPark = mediaRecorderRef.current
    flushPendingLiveLineToAccumulated(generationAtPark)
    cancelScheduledLivePartial()
    pendingLiveTranscriptRef.current = ""
    setLiveTranscript("")

    const streamIdForDrain = azureStreamIdRef.current
    const hadServerStreaming = hadAzureStreamingRef.current
    teardownAzureStreaming({ immediate: true })

    if (recognitionRef.current) {
      stopBrowserRecognition()
    }

    // Drop recording chrome before blob collection so visit switch is not blocked.
    setIsRecording(false)
    setIsPaused(false)

    if (recorderAtPark && recorderAtPark.state !== "inactive") {
      const currentBlob = await collectCurrentAudioBlob(recorderAtPark)
      if (
        captureGenerationRef.current === generationAtPark &&
        currentBlob
      ) {
        audioSegmentsRef.current.push(currentBlob)
      }
    }

    if (hadServerStreaming && streamIdForDrain) {
      await Promise.race([
        waitForStreamingDrain(streamIdForDrain, true),
        new Promise((resolve) => setTimeout(resolve, 120)),
      ])
    }
    if (captureGenerationRef.current === generationAtPark) {
      flushPendingLiveLineToAccumulated(generationAtPark)
    }

    const snapshot = {
      transcript:
        drainTranscriptByGenerationRef.current.get(generationAtPark) ||
        accumulatedTranscriptRef.current ||
        "",
      audioSegments: audioSegmentsRef.current.slice(),
      elapsedSeconds: timeCounter.current,
      lang: recognitionLangRef.current,
      startOptions: lastStartOptionsRef.current,
    }

    if (captureGenerationRef.current !== generationAtPark) {
      return snapshot
    }

    stopTimer()
    timerPausedRef.current = false
    isRecordingRef.current = false
    isPausedRef.current = false
    isParkedRef.current = true
    setIsRecording(false)
    setIsPaused(false)
    setIsParked(true)
    setTranscriptionSource(null)

    return snapshot
  }, [
    flushPendingLiveLineToAccumulated,
    cancelScheduledLivePartial,
    teardownAzureStreaming,
    stopBrowserRecognition,
    collectCurrentAudioBlob,
    waitForStreamingDrain,
    stopTimer,
  ])

  /** Snapshot current parked/active buffers without tearing down (for re-parking on leave). */
  const getParkedSnapshot = useCallback(() => {
    flushPendingLiveLineToAccumulated()
    return {
      transcript: accumulatedTranscriptRef.current || "",
      audioSegments: audioSegmentsRef.current.slice(),
      elapsedSeconds: timeCounter.current,
      lang: recognitionLangRef.current,
      startOptions: lastStartOptionsRef.current,
    }
  }, [flushPendingLiveLineToAccumulated])

  /** Restore a parked visit's transcript/audio into the UI (paused, ready to Resume/Stop). */
  const applyParkedSnapshot = useCallback((snapshot) => {
    if (!snapshot) return
    cancelScheduledLivePartial()
    teardownAzureStreaming({ immediate: true })
    if (recognitionRef.current) {
      stopBrowserRecognition()
    }
    stopTimer()

    const text = typeof snapshot.transcript === "string" ? snapshot.transcript : ""
    accumulatedTranscriptRef.current = text
    pendingLiveTranscriptRef.current = ""
    setTranscript(text)
    setLiveTranscript("")
    audioSegmentsRef.current = Array.isArray(snapshot.audioSegments)
      ? snapshot.audioSegments.slice()
      : []
    audioChunksRef.current = []
    timeCounter.current =
      typeof snapshot.elapsedSeconds === "number" && snapshot.elapsedSeconds >= 0
        ? snapshot.elapsedSeconds
        : 0
    setRecordingTime(formatRecordingClock(timeCounter.current))
    recognitionLangRef.current = snapshot.lang || recognitionLangRef.current
    if (snapshot.startOptions) {
      lastStartOptionsRef.current = snapshot.startOptions
    }

    isRecordingRef.current = false
    isPausedRef.current = false
    isParkedRef.current = true
    setIsRecording(false)
    setIsPaused(false)
    setIsParked(true)
    setTranscriptionSource(null)
  }, [cancelScheduledLivePartial, teardownAzureStreaming, stopBrowserRecognition, stopTimer])

  /** Resume hardware capture after a park, appending to saved transcript/audio. */
  const resumeParkedRecording = useCallback(
    async (onTranscriptUpdate, onAudioReady, lang, options = {}) => {
      const snap = getParkedSnapshot()
      const saved = lastStartOptionsRef.current || {}
      const mergedLang = lang || snap.lang || saved.lang || "en-US"
      const mergedOptions = {
        audioSourceMode: options.audioSourceMode || saved.audioSourceMode || "input",
        audioInputDeviceId:
          options.audioInputDeviceId !== undefined
            ? options.audioInputDeviceId
            : saved.audioInputDeviceId,
        azureStreaming:
          options.azureStreaming !== undefined
            ? options.azureStreaming
            : saved.azureStreaming,
        preserveTranscript: true,
        resumeElapsed: snap.elapsedSeconds,
      }
      await startRecording(
        onTranscriptUpdate || (() => {}),
        onAudioReady || lastOnAudioReadyRef.current,
        mergedLang,
        mergedOptions
      )
    },
    [getParkedSnapshot, startRecording]
  )

  const clearRecording = useCallback(() => {
    liveUiBoundRef.current = true
    captureGenerationRef.current += 1
    cancelScheduledLivePartial()
    teardownAzureStreaming({ immediate: true })
    if (recognitionRef.current) {
      try {
        stopBrowserRecognition()
      } catch (_) {}
    }
    stopTimer()
    stopAllMediaTracks()
    mediaRecorderRef.current = null
    audioChunksRef.current = []
    audioSegmentsRef.current = []
    setTranscript("")
    setLiveTranscript("")
    pendingLiveTranscriptRef.current = ""
    setRecordingTime("00:00")
    accumulatedTranscriptRef.current = ""
    timeCounter.current = 0
    isRecordingRef.current = false
    isPausedRef.current = false
    isParkedRef.current = false
    setIsRecording(false)
    setIsPaused(false)
    setIsParked(false)
    setTranscriptionSource(null)
  }, [teardownAzureStreaming, cancelScheduledLivePartial, stopBrowserRecognition, stopTimer, stopAllMediaTracks])

  /** Drop parked UI state without wiping transcript (SOAP generated / capture finished). */
  const endParkedCapture = useCallback(() => {
    isParkedRef.current = false
    setIsParked(false)
    isPausedRef.current = false
    setIsPaused(false)
  }, [])

  const pauseRecording = useCallback(() => {
    if (!isRecordingRef.current || isPausedRef.current) return
    flushPendingLiveLineToAccumulated()
    cancelScheduledLivePartial()
    pendingLiveTranscriptRef.current = ""
    setLiveTranscript("")

    if (mediaRecorderRef.current && mediaRecorderRef.current.state === "recording") {
      mediaRecorderRef.current.pause()
    }
    isPausedRef.current = true
    setIsPaused(true)
    pauseTimer()
    stopBrowserRecognition()

    const streamId = azureStreamIdRef.current
    if (streamId) {
      const s = getSocket()
      if (s?.connected) {
        s.emit("streaming_transcription:finalize", { streamId })
      }
    }
  }, [flushPendingLiveLineToAccumulated, cancelScheduledLivePartial, pauseTimer, stopBrowserRecognition])

  const resumeRecording = useCallback(() => {
    if (!isRecordingRef.current || !isPausedRef.current) return
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === "paused") {
      mediaRecorderRef.current.resume()
    }
    isPausedRef.current = false
    setIsPaused(false)
    resumeTimer()

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition
    if (SpeechRecognition && !hadAzureStreamingRef.current && !azureStreamIdRef.current) {
      startBrowserRecognition()
    }
  }, [resumeTimer, startBrowserRecognition])

  /** Re-sync live line from ref when returning to the tab/window. */
  const flushLiveTranscriptFromPending = useCallback(() => {
    const p = pendingLiveTranscriptRef.current
    if (p && isRecordingRef.current) {
      cancelScheduledLivePartial()
      setLiveTranscript(p)
    }
  }, [cancelScheduledLivePartial])

  /**
   * Full text for API payloads (e.g. SOAP): finalized transcript plus any interim line not yet merged.
   */
  const getFullTranscriptionText = useCallback(() => {
    const acc = (accumulatedTranscriptRef.current || "").trimEnd()
    if (!isRecordingRef.current) {
      const pending = (pendingLiveTranscriptRef.current || "").trim()
      if (acc && pending) return `${acc}\n${pending}`.trim()
      return (acc || pending).trim()
    }
    const tail = (
      pendingLiveTranscriptRef.current ||
      liveUiRef.current.liveTranscript ||
      ""
    ).trim()
    if (acc && tail) return `${acc}\n${tail}`.trim()
    return (acc || tail).trim()
  }, [])

  /** Replace entire transcript after stop (e.g. Spanish→English pass before SOAP). */
  const setFullTranscriptText = useCallback((text) => {
    syncTranscript(typeof text === "string" ? text : "")
  }, [syncTranscript])

  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        flushLiveTranscriptFromPending()
        if (isRecordingRef.current && audioContextRef.current?.state === "suspended") {
          void audioContextRef.current.resume()
        }
      }
    }
    const onFocus = () => {
      flushLiveTranscriptFromPending()
      if (isRecordingRef.current && audioContextRef.current?.state === "suspended") {
        void audioContextRef.current.resume()
      }
    }
    document.addEventListener("visibilitychange", onVisibility)
    window.addEventListener("focus", onFocus)
    return () => {
      document.removeEventListener("visibilitychange", onVisibility)
      window.removeEventListener("focus", onFocus)
    }
  }, [flushLiveTranscriptFromPending])

  const MicPermissionModalGate = useCallback(
    () => (
      <MicPermissionModal
        isOpen={micModalOpen}
        isBlocked={micModalBlocked}
        isRequesting={micModalRequesting}
        onEnable={handleMicModalEnable}
        onClose={handleMicModalClose}
      />
    ),
    [
      micModalOpen,
      micModalBlocked,
      micModalRequesting,
      handleMicModalEnable,
      handleMicModalClose,
    ]
  )

  const DisplayAudioModalGate = useCallback(
    () => (
      <DisplayAudioModal
        isOpen={displayAudioModalOpen}
        allowMicOnly={displayAudioModalAllowMicOnly}
        allowContinueAnyway={displayAudioModalAllowContinueAnyway}
        isResharing={displayAudioModalResharing}
        retryFailed={displayAudioModalRetryFailed}
        captureEnvironment={detectCaptureEnvironment()}
        onReshare={handleDisplayAudioReshare}
        onMicOnly={handleDisplayAudioMicOnly}
        onContinueAnyway={handleDisplayAudioContinue}
        onCancel={handleDisplayAudioCancel}
      />
    ),
    [
      displayAudioModalOpen,
      displayAudioModalAllowMicOnly,
      displayAudioModalAllowContinueAnyway,
      displayAudioModalResharing,
      displayAudioModalRetryFailed,
      handleDisplayAudioReshare,
      handleDisplayAudioMicOnly,
      handleDisplayAudioContinue,
      handleDisplayAudioCancel,
    ]
  )

  const RecordingModalsGate = useCallback(
    () => (
      <>
        {MicPermissionModalGate()}
        {DisplayAudioModalGate()}
      </>
    ),
    [MicPermissionModalGate, DisplayAudioModalGate]
  )

  return {
    isRecording,
    isPaused,
    isParked,
    /** Prefer useRecordingLiveUi — these snapshots do not trigger parent re-renders. */
    get transcript() {
      return liveUiRef.current.transcript
    },
    transcriptionSource,
    get liveTranscript() {
      return liveUiRef.current.liveTranscript
    },
    get recordingTime() {
      return liveUiRef.current.recordingTime
    },
    subscribeLiveUi,
    getLiveUiSnapshot,
    checkMicPermission,
    ensureDisplayAudio,
    getAudioInputDevices,
    startRecording,
    stopRecording,
    pauseRecording,
    resumeRecording,
    parkRecording,
    getParkedSnapshot,
    applyParkedSnapshot,
    resumeParkedRecording,
    endParkedCapture,
    clearRecording,
    detachLiveTranscriptUi,
    releaseCaptureUi,
    getCaptureGeneration,
    setTranscript: syncTranscript,
    stopTranscribing,
    flushLiveTranscriptFromPending,
    flushPendingLiveLineToAccumulated,
    getFullTranscriptionText,
    setFullTranscriptText,
    MicPermissionModalGate,
    DisplayAudioModalGate,
    RecordingModalsGate,
  }
}

const EMPTY_LIVE_UI = Object.freeze({
  transcript: "",
  liveTranscript: "",
  recordingTime: "00:00",
})

/**
 * Subscribe to high-frequency live-capture UI without re-rendering the parent
 * that owns useRecording().
 */
export function useRecordingLiveUi(recording) {
  const subscribe = recording?.subscribeLiveUi || (() => () => {})
  const getSnapshot = recording?.getLiveUiSnapshot || (() => EMPTY_LIVE_UI)
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot)
}

